import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dispatch } from 'redux';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { GIButton } from 'app-components';
import { StyledIdentityHeader } from '../Layout';
import AlertInfo from '@components/common/alert/AlertInfo';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import UniversalInput from '@lib/wrappers/UniversalInput';
import Form from '@lib/wrappers/Form';
import { useAppSelector, useAppDispatch } from '@hooks/index';
import { validateRidpSecondary } from '@actions/ridp/ridpActionCreators';
import { configs } from '@constants/configs/configs';
import { useHandleRIDPSecondaryResponse } from '../../utils/useHandleRidpResponse';
import { ResponseCodes } from '../../types/responseCodes';

interface IQuestionState {
    questionNumber: number;
    currentAnswer: {
        value: number | undefined;
        text: string | undefined;
    };
}

interface IQuestion {
    questionNumber: number;
    question: string;
    choices: { answerChoice: { choiceIndex: number; choiceText: string } }[];
}

const StyledButton = styled.div`
    width: 100%;
    display: flex;
    justify-content: end;
    padding-right: 25px;
    padding-bottom: 25px;
`;

const StyledQuestionContainer = styled.div`
    padding-top: 10px;
`;

const StyledContainer = styled.div`
    .gi-alert {
        padding: 1rem;
    }
`;

const IdentityQuestions = () => {
    const [questions, setQuestions] = useState<IQuestion[]>([]);
    const [questionState, setQuestionState] = useState<IQuestionState[]>([]);
    const currentUserInfo = useAppSelector(state => state.ridp.currentUserInfo);
    const identityQuestionsResponse = useAppSelector(state => state.ridp.ridpPrimaryResponse);
    const ridpSecondaryResponse = useAppSelector(state => state.ridp.ridpSecondaryResponse);

    const dispatch: Dispatch<any> = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const existingParams = new URLSearchParams(location.search);
    const { endpoints, routes: { RIDP_CONTACT_INFO }, } = configs;

    const navigateToContactInformation = () => {
        navigate(`${RIDP_CONTACT_INFO}?${existingParams.toString()}`);
    };

    useEffect(() => {
        // Identity Questions Page should not be accessible for RBA flow
        if (currentUserInfo?.isRidpRbaEnabled === true) { 
            navigateToContactInformation();
        }
    }, [currentUserInfo?.isRidpRbaEnabled]);

    useHandleRIDPSecondaryResponse(ridpSecondaryResponse);

    useEffect(() => {
        if (identityQuestionsResponse?.ResponseMetadata?.ResponseCode === ResponseCodes.HS000000) {
            setQuestions(identityQuestionsResponse.verificationResponse.questions);
        } else {
            navigateToContactInformation();
        }
    }, [identityQuestionsResponse]);

    useEffect(() => {
        if (questions.length > 0) {
            const initialQuestionState = questions.map(questionData => ({
                questionNumber: questionData.questionNumber,
                currentAnswer: {
                    [`value_answer${questionData.questionNumber}`]: undefined,
                    text: '',
                },
            }));
            setQuestionState(initialQuestionState as any);
        }
    }, [questions]);

    const formattedQuestions = useMemo(() => {
        return questions.map(questionData => {
            return {
                questionNumber: questionData.questionNumber,
                questionLegend: questionData.question,
                questionOptions: questionData.choices.map(question => {
                    return {
                        value: question.answerChoice.choiceIndex,
                        text: question.answerChoice.choiceText,
                    };
                }),
            };
        });
    }, [questions]);

    const submitIdentityQuestions = useCallback(
        e => {
            e.preventDefault();
            const payload = {
                sessionIdentifier: identityQuestionsResponse.verificationResponse.sessionIdentifier,
                verificationResponses: questionState.map(question => {
                    return {
                        questionNumber: question.questionNumber,
                        choiceIndex: question.currentAnswer[`value_answer${question.questionNumber}`],
                    };
                }),
            };
            dispatch(validateRidpSecondary(endpoints.RIDP_VALIDATE_SECONDARY_ENDPOINT, payload));
        },
        [questionState, identityQuestionsResponse],
    );

    const handleFieldChange = useCallback(
        (data, questionIndex, formattedQuestion) => {
            const updatedAnswer = parseInt(data[`value_answer${formattedQuestion.questionNumber}`]);
            const updatedText = formattedQuestions[questionIndex].questionOptions.find(
                question => question.value === updatedAnswer,
            )?.text;

            const updatedQuestionState = questionState.map((question, index) =>
                index === questionIndex
                    ? {
                        ...question,
                        currentAnswer: {
                            [`value_answer${formattedQuestion.questionNumber}`]: updatedAnswer,
                            text: updatedText,
                        },
                    }
                    : question,
            );

            setQuestionState(updatedQuestionState as any);
        },
        [questionState, formattedQuestions],
    );

    const isSubmitDisabled = useMemo(() => {
        return questionState.length === 0 || questionState.some(question => question?.currentAnswer?.text === '');
    }, [questionState]);

    return (
        <main id="main-content" className="gi-panel">
            <StyledContainer>
                <StyledIdentityHeader id="identity-questions-header" className="h4">
                    <FormattedMessage id="ridp.sidebar.identityQuestions" />
                </StyledIdentityHeader>
                <p className="mt-5 mb-4">
                    <FormattedMessage id="ridp.identityquestions.subHeader" />
                </p>
                <AlertInfo id="ridp.identityquestions.alert.para" />
                <StyledQuestionContainer>
                    <Form {...{ formName: 'identityQuestions', action: {} }} aria-labelledby="identity-questions-header">
                        {!!formattedQuestions.length &&
                            questionState.length &&
                            formattedQuestions.map((formattedQuestion, questionIndex) => (
                                <UniversalInput
                                    key={`question_key_${formattedQuestion.questionNumber}_${questionIndex}`}
                                    legend={{
                                        legendText: `${formattedQuestion.questionNumber}. ${formattedQuestion.questionLegend}`,
                                    }}
                                    label={{
                                        showRequiredIcon: true,
                                        ulClass: ['margin-b-20'],
                                    }}
                                    inputData={{
                                        isInputRequired: true,
                                        type: INPUT_TYPES.RADIO_BUTTONS,
                                        liClass: ['text-left'],
                                        currentData: questionState[questionIndex].currentAnswer,
                                        name: `value_answer${formattedQuestion.questionNumber}`,
                                        value: parseInt(
                                            questionState[questionIndex].currentAnswer[
                                                `value_answer${formattedQuestion.questionNumber}`
                                            ],
                                        ),
                                        options: formattedQuestion.questionOptions,
                                    }}
                                    errorClasses={['margin-l-0']}
                                    inputActions={{
                                        onFieldChange: data =>
                                            handleFieldChange(data, questionIndex, formattedQuestion),
                                    }}
                                />
                            ))}
                        <StyledButton>
                            <GIButton
                                type="submit"
                                id="ridpContactInfo"
                                labelId="button.submit"
                                onClickHandler={e => submitIdentityQuestions(e)}
                                isDisabled={isSubmitDisabled as boolean}
                            />
                        </StyledButton>
                    </Form>
                </StyledQuestionContainer>
            </StyledContainer>
        </main>
    );
};

export default IdentityQuestions;
